/**
 * @fileoverview UI hook for drag-and-drop images over the inbox messages area (react-dropzone).
 * Drag-only (no click); on drop opens the same inbox image flow as ChatAttachMenuMC via sharingInformationService.
 * @module useInboxChatMessagesDropzoneUI
 * @category Hook - UI Layer
 */
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { sharingInformationService } from '../../../../services/sharingInformationService';
import { MAX_IMAGE_SIZE_TRANSFER_WS } from '../../../../constants/chatFileSizes';
import { showtoast } from '../../../../util/toast';

const IMAGE_ACCEPT = {
  'image/jpeg': [],
  'image/png': []
};

const ALLOWED_PASTE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);

/**
 * @param {DataTransfer | null | undefined} clipboardData
 * @returns {File[]}
 */
function collectPastedImageFiles(clipboardData) {
  if (!clipboardData) return [];

  const fromFiles = clipboardData.files?.length
    ? Array.from(clipboardData.files).filter((f) => ALLOWED_PASTE_IMAGE_TYPES.has(f.type))
    : [];

  if (fromFiles.length) return fromFiles;

  const { items } = clipboardData;
  if (!items?.length) return [];

  const out = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.kind === 'file' && ALLOWED_PASTE_IMAGE_TYPES.has(item.type)) {
      const file = item.getAsFile();
      if (file) out.push(file);
    }
  }
  return out;
}

/**
 * @param {Object} params
 * @param {boolean} params.canDrop - When false, drag overlay and drops are ignored.
 * @param {function(File[]): void} [params.onDropAcceptedFiles] - Override default sharingInformationService publish.
 * @returns {{ getRootProps: function, getInputProps: function, showDragOverlay: boolean, handlePasteCapture: function }}
 */
export default function useInboxChatMessagesDropzoneUI({
  canDrop,
  onDropAcceptedFiles
}) {
  const { t } = useTranslation();
  const [showDragOverlay, setShowDragOverlay] = useState(false);

  const notifyOversizedFiles = useCallback(() => {
    const maxFileSizeMb = Math.round(MAX_IMAGE_SIZE_TRANSFER_WS / (1024 * 1024));
    showtoast(t('file_uploader.maxFileSize', { maxFileSize: maxFileSizeMb }));
  }, [t]);

  const filterFilesWithinMaxSize = useCallback((files) => {
    const valid = files.filter((file) => file.size <= MAX_IMAGE_SIZE_TRANSFER_WS);
    if (valid.length < files.length) {
      notifyOversizedFiles();
    }
    return valid;
  }, [notifyOversizedFiles]);

  const publishDroppedFiles = useCallback((files) => {
    const validFiles = filterFilesWithinMaxSize(files);
    if (!validFiles.length) return;

    if (onDropAcceptedFiles) {
      onDropAcceptedFiles(validFiles);
      return;
    }
    sharingInformationService.setSubject({
      openInboxImageAttachSidebar: true,
      inboxImageAttachInitialFiles: validFiles
    });
    sharingInformationService.setSubject({});
  }, [onDropAcceptedFiles, filterFilesWithinMaxSize]);

  const handleDragEnter = useCallback(() => {
    if (!canDrop) return;
    setShowDragOverlay(true);
  }, [canDrop]);

  const handleDragLeave = useCallback(() => {
    setShowDragOverlay(false);
  }, []);

  const handleDrop = useCallback((acceptedFiles, fileRejections) => {
    setShowDragOverlay(false);
    if (!canDrop) return;

    const hasTooLarge = fileRejections?.some(
      (rejection) => rejection.errors?.some((error) => error.code === 'file-too-large')
    );
    if (hasTooLarge) {
      notifyOversizedFiles();
    }

    if (!acceptedFiles?.length) return;
    publishDroppedFiles(acceptedFiles);
  }, [canDrop, publishDroppedFiles, notifyOversizedFiles]);

  const handlePasteCapture = useCallback((e) => {
    if (!canDrop) return;
    const imageFiles = collectPastedImageFiles(e.clipboardData);
    if (!imageFiles.length) return;
    e.preventDefault();
    e.stopPropagation();
    publishDroppedFiles(imageFiles);
  }, [canDrop, publishDroppedFiles]);

  const { getRootProps, getInputProps } = useDropzone({
    noClick: true,
    accept: IMAGE_ACCEPT,
    maxSize: MAX_IMAGE_SIZE_TRANSFER_WS,
    disabled: !canDrop,
    onDrop: handleDrop,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave
  });

  return {
    getRootProps,
    getInputProps,
    showDragOverlay: canDrop && showDragOverlay,
    handlePasteCapture
  };
}
